// src/utils/download.js
// ✅ 인증이 걸린 파일 다운로드 공통 헬퍼
//
// ⚠️ window.open(`${api.defaults.baseURL}...`) 방식은 쓰면 안 된다 — 이 프로젝트는
// JWT 전용 인증이라(settings.REST_FRAMEWORK의 DEFAULT_AUTHENTICATION_CLASSES가
// JWTAuthentication 하나뿐, SessionAuthentication 없음) 브라우저가 새 탭으로 여는
// 요청에는 Authorization 헤더가 실리지 않아 무조건 401이 뜬다.
// 반드시 api 인스턴스(요청 인터셉터가 토큰을 붙이고, 만료 시 자동 갱신까지 한다)로
// blob을 받아서 Blob URL로 저장시킨다.
import api from "../api";

// Content-Disposition에서 파일명 추출.
//   filename*=UTF-8''%ED%95%9C%EA%B8%80.xlsx   (RFC 5987, 퍼센트 인코딩)
//   filename="한글.xlsx"                        (구식)
// 둘 다 받아준다. 서버가 인코딩을 빼먹어도(비표준) 그대로 쓸 수 있게 decode 실패는 무시.
export function filenameFromDisposition(disposition, fallback = "download") {
  if (!disposition) return fallback;

  const star = /filename\*=\s*([^']*)'([^']*)'([^;]+)/i.exec(disposition);
  if (star) {
    const raw = star[3].trim().replace(/^"|"$/g, "");
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;   // 서버가 퍼센트 인코딩을 안 한 경우
    }
  }

  const plain = /filename=\s*"?([^";]+)"?/i.exec(disposition);
  if (plain) return plain[1].trim();

  return fallback;
}

/**
 * 인증 붙은 GET → 파일 저장.
 * @param {string} url      api baseURL 기준 상대 경로 (예: "quotation/versions/3/export-xlsx/")
 * @param {string} fallback Content-Disposition이 없을 때 쓸 파일명
 */
export async function downloadFile(url, fallback = "download") {
  // ⚠️ 파일 생성이 오래 걸릴 수 있어 api의 기본 timeout(10초)을 넉넉히 늘린다.
  const res = await api.get(url, { responseType: "blob", timeout: 120000 });

  const name = filenameFromDisposition(
    res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"],
    fallback
  );

  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);   // Firefox는 DOM에 붙어 있어야 click이 먹는다
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
  return name;
}

/**
 * downloadFile + 실패 시 alert까지. 화면 쪽 코드를 짧게 유지하기 위한 래퍼.
 * ⚠️ responseType:"blob"이면 에러 응답 본문도 Blob으로 와서 err.response.data.detail을
 * 그냥 읽을 수 없다 — text()로 풀어서 메시지를 꺼낸다.
 */
export async function downloadFileWithAlert(url, fallback, failMessage = "다운로드에 실패했습니다.") {
  try {
    return await downloadFile(url, fallback);
  } catch (err) {
    let detail = "";
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        detail = JSON.parse(text)?.detail || text;
      } catch {
        /* 본문을 못 읽으면 상태코드만 안내 */
      }
    } else if (typeof data === "string") {
      detail = data;
    } else if (data?.detail) {
      detail = data.detail;
    }
    const status = err?.response?.status;
    alert(`${failMessage}${status ? ` (${status})` : ""}${detail ? `\n${detail}` : ""}`);
    return null;
  }
}
